// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useMemo, useState } from 'react';

export const ALL_FOLDERS = 'all';
export const NO_FOLDER = 'none';
export const NO_FOLDER_LABEL = 'No folder';

export interface IFolderOption {
  value: string;
  label: string;
}

const matchesSearch = (search: string) => {
  const text = search.toLowerCase();
  return ({ referenceId, name }: IAdminProject) => !text ||
    (referenceId && referenceId.toLowerCase().includes(text)) ||
    (name && name.toLowerCase().includes(text));
};

// Only the organization's own folders name a row; a folderId outside them counts as no folder
const folderKeyOf = (folderNames: Map<string, string>, { folderId }: IAdminProject) =>
  (folderId && folderNames.has(folderId) ? folderId : NO_FOLDER);

// Search and the Folder filter of Treasure Chest's Projects tab. The folder choice is kept with the organization it
// was made for, so another organization (or a reload that no longer has the chosen folder) shows all folders again.
export const useProjectFilters = (orgId: string, projects: IAdminProject[], folders: IFolder[] | null) => {
  const [search, setSearch] = useState('');
  const [choice, setChoice] = useState({ orgId, value: ALL_FOLDERS });

  const folderNames = useMemo(() => new Map((folders ?? []).map((folder) => [folder._id, folder.name])), [folders]);
  const folderName = (project: IAdminProject) => folderNames.get(folderKeyOf(folderNames, project)) ?? NO_FOLDER_LABEL;

  const folderChoice = choice.orgId === orgId && (choice.value === NO_FOLDER || folderNames.has(choice.value)) ? choice.value : ALL_FOLDERS;
  const setFolderChoice = (value: string) => setChoice({ orgId, value });

  const searched = useMemo(() => (projects ?? []).filter(matchesSearch(search)), [projects, search]);

  // Each option counts the rows it would show under the current search
  const folderOptions = useMemo<IFolderOption[]>(() => {
    const counts = new Map<string, number>();
    searched.forEach((project) => {
      const key = folderKeyOf(folderNames, project);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return [
      { value: ALL_FOLDERS, label: 'All folders' },
      { value: NO_FOLDER, label: `${NO_FOLDER_LABEL} (${counts.get(NO_FOLDER) ?? 0})` },
      ...(folders ?? []).map((folder) => ({ value: folder._id, label: `${folder.name} (${counts.get(folder._id) ?? 0})` })),
    ];
  }, [searched, folders, folderNames]);

  const rows = useMemo(
    () => (folderChoice === ALL_FOLDERS ? searched : searched.filter((project) => folderKeyOf(folderNames, project) === folderChoice)),
    [searched, folderChoice, folderNames],
  );

  return {
    setSearch,
    folderChoice,
    setFolderChoice,
    folderOptions,
    folderName,
    rows,
    showFolders: Array.isArray(folders),
    hasFolders: folderNames.size > 0,
  };
};
